import axios from 'axios';

// If REACT_APP_BACKEND_URL is unset or blank, fall back to same-origin /api.
// Same-origin requests are exempt from CORS — useful when the platform proxies
// /api/* on the frontend domain to the backend container.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();

const api = axios.create({
  baseURL: BACKEND_URL ? `${BACKEND_URL}/api` : '/api',
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

/**
 * Format axios errors with backend connectivity awareness.
 * Handles 5xx + non-JSON responses so users see actionable messages
 * instead of the generic fallback when the backend itself is down.
 */
export function formatAxiosError(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;
  // No HTTP response — network/CORS/preflight failure
  if (!err.response) {
    return 'Cannot reach the server. Please check your internet connection or try again in a moment.';
  }
  const { status, data } = err.response;
  // Server returned HTML instead of JSON (typically 5xx via Cloudflare/ingress)
  if (typeof data === 'string' && data.trim().startsWith('<')) {
    return `Server is temporarily unavailable (HTTP ${status}). Please try again shortly.`;
  }
  if (data && data.detail != null) return formatApiErrorDetail(data.detail);
  if (status >= 500) return `Server error (HTTP ${status}). Please try again shortly.`;
  return fallback;
}

export default api;
